import { atom, useAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useQueryJoinedTeams } from '../../data/query/useQueryJoinedTeams';
import {
  type PendingTeamJoinNote,
  clearPendingTeamJoinNote,
  readPendingTeamJoinNote,
} from '../../data/team-join/pending-team-join-note';
import { type PendingTeamJoinPorts } from './ports';
import { buildTeamWelcomeHref } from './team-welcome-route';

/** `undefined` until the note has been read out of the tab's storage. */
const pendingTeamJoinAtom = atom<PendingTeamJoinNote | null | undefined>(undefined);

interface PendingTeamJoinOutcomeProps {
  note: PendingTeamJoinNote;
  onSettled: () => void;
  ports: PendingTeamJoinPorts;
}

/**
 * Mounted only while a note exists, so people who accepted no invite never
 * trigger the joined-teams request.
 */
function PendingTeamJoinOutcome({ note, onSettled, ports }: PendingTeamJoinOutcomeProps) {
  const replace = ports.useReplaceRoute();
  const { data, isError, isPending, isFetching } = useQueryJoinedTeams();
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    if (hasFailed || isPending || isFetching) return;

    // The query swallows its errors into no data, which settles it as an
    // error. A failed read is reported like a failed join, and the note stays
    // in storage so a reload can retry.
    if (isError) {
      setHasFailed(true);
      return;
    }

    const isMember =
      data?.joined_teams.some((team) => team.team_id === note.teamId) ?? false;
    if (!isMember) {
      setHasFailed(true);
      return;
    }
    onSettled();
    void replace(
      buildTeamWelcomeHref({ teamId: note.teamId, isNewUser: note.isNewUser })
    );
  }, [data, hasFailed, isError, isFetching, isPending, note, onSettled, replace]);

  if (!hasFailed) return null;
  return ports.renderJoinFailed({ teamId: note.teamId, onReturn: onSettled });
}

/**
 * After a login that came through the invite on /auth/onboard, confirms the
 * join happened: a member goes to the welcome page, anyone else sees why not.
 */
export function PendingTeamJoinCheck({ ports }: { ports: PendingTeamJoinPorts }) {
  const [pending, setPending] = useAtom(pendingTeamJoinAtom);
  const hasRead = useRef(false);

  useEffect(() => {
    if (hasRead.current || pending !== undefined) return;
    hasRead.current = true;
    setPending(readPendingTeamJoinNote());
  }, [pending, setPending]);

  const handleSettled = useCallback(() => {
    clearPendingTeamJoinNote();
    setPending(null);
  }, [setPending]);

  if (!pending) return null;
  return (
    <PendingTeamJoinOutcome note={pending} onSettled={handleSettled} ports={ports} />
  );
}
